import { UseQueryOptions } from '@tanstack/react-query';
import { ProductAlternatesResponse, PromotionsResponse } from './types';
import { AxiosMethods } from '../../../../enums';
import { ProductApi } from '../../../../enums/api/products';
import { axiosHelper } from '../../../axios-instance';

enum ProductsQueryKey {
  PRODUCT = 'product',
  SEARCH_PRODUCTS = 'search-products',
  REALTIME_INVENTORY = 'realtime-inventory',
  PROMOTIONS = 'promotions',
  CONFIGURATIONS = 'product-configurations',
  ALTERNATES = 'product-alternates'
}

/** The endpoint rejects more than this in one request. */
export const PRODUCT_ALTERNATES_BATCH_SIZE = 200;

interface GetProductQueryParams {
  productId: string;
}

export const getProductQuery = ({ productId }: GetProductQueryParams) => ({
  queryKey: [ProductsQueryKey.PRODUCT, productId],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.getProduct.replace(
        ':productId',
        encodeURIComponent(productId)
      ),
      method: AxiosMethods.GET
    }),
  enabled: !!productId
});

interface GetPromotionsQueryParams {
  skus: string[];
}

export const getPromotionsQuery = ({
  skus
}: GetPromotionsQueryParams): UseQueryOptions<PromotionsResponse> => ({
  queryKey: [ProductsQueryKey.PROMOTIONS, skus],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.getPromotions,
      method: AxiosMethods.POST,
      data: {
        filters: {
          field: '_id',
          operator: '$in',
          value: skus
        },
        offset: 0,
        limit: 100
      }
    })
});

interface ProductSearchFilter {
  field: string;
  operator: string;
  value: any;
}

interface ProductSearchSort {
  field: string;
  direction: 'asc' | 'desc';
}

interface SearchProductsQueryParams {
  searchStr?: string;
  limit: number;
  offset: number;
  filters?: ProductSearchFilter;
  sort?: ProductSearchSort[];
}

export const searchProductsQuery = ({
  searchStr,
  limit,
  offset,
  filters,
  sort
}: SearchProductsQueryParams) => ({
  queryKey: [
    ProductsQueryKey.SEARCH_PRODUCTS,
    searchStr,
    limit,
    offset,
    filters,
    sort
  ],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.searchProducts,
      method: AxiosMethods.POST,
      data: {
        ...(searchStr && { searchStr }),
        limit,
        offset,
        ...(filters && { filters }),
        ...(sort && { sort })
      }
    })
});

interface GetProductLastSoldDetailsQueryParams {
  productId: string;
  accountId: string;
  addressId: string;
  limit?: number;
  offset?: number;
}

export const getProductLastSoldDetailsQuery = ({
  productId,
  accountId,
  addressId,
  limit = 10,
  offset = 0
}: GetProductLastSoldDetailsQueryParams) => ({
  queryKey: [ProductsQueryKey.PRODUCT, productId, accountId, addressId],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.getProductLastSoldDetails,
      method: AxiosMethods.POST,
      data: {
        accountId,
        addressId,
        productId,
        limit,
        offset
      }
    }),
  enabled: !!productId && !!accountId && !!addressId,
  staleTime: 10 * 60 * 1000,
  cacheTime: 10 * 60 * 1000
});

interface GetProductAlternatesQueryParams {
  skus: string[];
}

/**
 * Catalog-linked alternates for a batch of SKUs (DEV-637).
 *
 * Identity only — price the results through `POST /pricing/get` so the numbers
 * come from the same source as the lines they're compared against.
 *
 * The key uses a SORTED sku list so the same set of lines hits the same cache
 * entry regardless of the order they happen to be in.
 */
export const getProductAlternatesQuery = ({
  skus
}: GetProductAlternatesQueryParams): UseQueryOptions<ProductAlternatesResponse> => ({
  queryKey: [ProductsQueryKey.ALTERNATES, [...skus].sort().join(',')],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.getAlternates,
      method: AxiosMethods.POST,
      data: { skus }
    }),
  enabled: !!skus.length
});

export const getProductConfigurations = () => ({
  queryKey: [ProductsQueryKey.CONFIGURATIONS],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.getConfigurations,
      method: AxiosMethods.GET
    })
});
